<template lang="pug">
.total-weight(v-if="value")
  span.label {{ __('Total Weight:') }}
  span.value {{ value }}
</template>
<script setup lang="ts">
import { computed, watch } from 'vue'
import { formatWeight, getBuyRequestWeightLb, isTotalWeightEnabled } from '@/js/helpers/buy-request-weight'
import { useRogue } from '@/js/services/Rogue'
import type { BuyRequest } from '@/js/services/RogueTypes'

const props = defineProps<{
  buyRequest: BuyRequest
}>()

const rogue = useRogue()

const getProduct = (id: number) => rogue.getProductById(id)

// Accessory-layout grouped PDPs weigh only the main item, picked the same way as grouped-parent.vue's isMainAccessoryProduct.
const mainBuyRequest = computed(() => {
  const product = getProduct(props.buyRequest.productId)
  if (props.buyRequest.type !== 'group' || !product?.roguebuilder?.props?.isAccessoryProduct) return props.buyRequest

  const mainId = product.attributes?.fmg_used_for_price

  return props.buyRequest.children?.find((child, i) => (mainId ? mainId === `${child.productId}` : i === 0))
})

const weight = computed(() => (mainBuyRequest.value && !props.buyRequest.loading && isTotalWeightEnabled(mainBuyRequest.value, getProduct)
  ? getBuyRequestWeightLb(mainBuyRequest.value, getProduct)
  : { lb: 0, invalidProductIds: [] }))

const value = computed(() => (weight.value.lb > 0 ? rogue.__('{lb} LB / {kg} KG', formatWeight(weight.value.lb)) : ''))

watch(() => weight.value.invalidProductIds, (ids) => {
  if (rogue.ssrContext) return
  ids.forEach(id => rogue.log.warn(`Missing or non-numeric adlinfo_weight for product ${getProduct(id)?.sku ?? id}`))
}, { immediate: true })
</script>
<style lang="scss" scoped>
.total-weight {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25em;
  margin: $size-xxl 0;
  font-family: $font-condensed;
  font-size: $size-l;
  line-height: 1;
  color: $color-black;

  .label {
    font-weight: $weight-normal;
  }

  .value {
    font-weight: $weight-bold;
  }
}
</style>
